"use client";

import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Segmented } from "@/components/ui/design-system/segmented";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { animateThemeSwitch } from "@/lib/themeTransition";
import { SettingRow } from "./settingsRow";

const THEMES = ["light", "dark", "system"] as const;
type Theme = (typeof THEMES)[number];

const isTheme = (value: string | undefined): value is Theme =>
  (THEMES as readonly string[]).includes(value ?? "");

/**
 * Light, Dark or System, as a row in Settings.
 *
 * The same control as the Language row under it. Two choices of one kind,
 * stacked in one sheet, drawn as two different widgets read as two different
 * kinds of setting; the DS `ThemeToggle` is still what Framework uses, and
 * this keeps its behaviour, not its look: next-themes for the value, and the
 * masked reveal from the control when the choice lands.
 */
export function AppearanceSetting() {
  const t = useCreatorTranslations("settings");
  const { theme, setTheme } = useTheme();
  const originRef = useRef<HTMLDivElement>(null);

  // The server cannot know the stored theme, so the first paint shows System
  // rather than a guess that flips on hydration.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <SettingRow
      label={t("appearance.label")}
      hint={t("deviceOnly")}
      control={
        <div ref={originRef} className="flex sm:justify-end">
          <Segmented
            name="creator-appearance"
            legend={t("appearance.label")}
            value={mounted && isTheme(theme) ? theme : "system"}
            onChange={(next) =>
              // next-themes writes the class in a passive effect, so the
              // commit is flushed before the reveal snapshots the new theme;
              // see `ThemeToggle`, which does the same.
              void animateThemeSwitch(
                () => flushSync(() => setTheme(next)),
                originRef.current,
              )
            }
            options={THEMES.map((id) => ({
              id,
              name: t(`appearance.${id}`),
            }))}
          />
        </div>
      }
    />
  );
}
